import { Inject, Injectable, inject } from '@angular/core';
import { FirebaseApp } from 'firebase/app';
import { Auth, User, getAuth } from 'firebase/auth';
import {
  MonoTypeOperatorFunction,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  fromEventPattern,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import {
  DomainSignalId,
  EffectivePermissionsResponse,
  EntityApplications,
  RoleEntityPermissionMap,
} from '@ag-common-lib/public-api';
import { FIREBASE_APP } from '../injections/firebase-app';
import { DomainSignalsService } from './domain-signals.service';
import { IdentityApiService } from './identity-api.service';

/**
 * An effective-permission read, plus whether it is an ANSWER or a FAILURE.
 *
 * "You have no permissions" and "we could not find out what your permissions are"
 * both render as an empty map, but they must never be acted on the same way: the
 * first is a reason to sign someone out, the second is a reason to leave them
 * alone until the next read succeeds.
 */
export type EffectivePermissions = EffectivePermissionsResponse & { readonly resolved: boolean };

const emptyPermissions = (
  appId: EntityApplications,
  agentFirebaseDbId = '',
  resolved = true,
): EffectivePermissions => ({
  agentFirebaseDbId,
  appId,
  roleIds: [],
  roleNames: [],
  permissions: {} as RoleEntityPermissionMap,
  resolved,
});

/**
 * The single source of "what may this user do", replacing four hand-written folds
 * that each merged role documents in the client (arm-auth, arm-permissions,
 * portal-permissions and portal-pwa-auth).
 *
 * The merge now happens server-side, so "effective" means the same thing to the API
 * enforcing a call and to the UI deciding whether to show the button that makes it.
 *
 * Re-reads whenever the user-roles signal moves, which is what makes a permission
 * change land in an open session in seconds rather than at next login.
 */
@Injectable({ providedIn: 'root' })
export class EffectivePermissionsService {
  private readonly identityApi = inject(IdentityApiService);
  private readonly domainSignals = inject(DomainSignalsService);

  private readonly auth: Auth;
  private readonly currentUser$: Observable<User | null>;

  /**
   * Cached per (agent, app). Permissions are read from route guards, hundreds of
   * template pipes and the login gate; without sharing, each of those would issue
   * its own HTTP request on every emission of the signal.
   */
  private readonly streamsByKey = new Map<string, Observable<EffectivePermissions>>();

  constructor(@Inject(FIREBASE_APP) fireBaseApp: FirebaseApp) {
    this.auth = getAuth(fireBaseApp);
    this.currentUser$ = fromEventPattern<User | null>(
      handler => this.auth.onAuthStateChanged(handler as never),
      (_handler, unsubscribe: () => void) => unsubscribe(),
    ).pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  /**
   * The signed-in caller. Resolves to the impersonated agent when a session is
   * impersonating, because the server resolves the effective agent for `/me`.
   */
  forCurrentUser(appId: EntityApplications): Observable<EffectivePermissions> {
    return this.getCached(`me:${appId}`, () =>
      combineLatest([this.currentUser$, this.changeTrigger$()]).pipe(
        switchMap(([user]) =>
          user
            ? from(this.identityApi.getMyPermissions(appId)).pipe(
                map(response => ({ ...response, resolved: true }) as EffectivePermissions),
                this.failClosed(appId),
              )
            : /**
               * Signed out is not an answer about a user, it is the absence of one.
               * Marked unresolved so the revocation watcher cannot read an ordinary
               * sign-out as "this agent just lost all access" and sign them out again
               * from inside the sign-out.
               */
              of(emptyPermissions(appId, '', false)),
        ),
      ),
    );
  }

  /** Another agent — the portal's viewed-agent case. Gated server-side. */
  forAgent(agentFirebaseDbId: string | null | undefined, appId: EntityApplications): Observable<EffectivePermissions> {
    if (!agentFirebaseDbId) {
      return of(emptyPermissions(appId));
    }

    return this.getCached(`agent:${agentFirebaseDbId}:${appId}`, () =>
      this.changeTrigger$().pipe(
        switchMap(() =>
          from(this.identityApi.getAgentPermissions(agentFirebaseDbId, appId)).pipe(
            map(response => ({ ...response, resolved: true }) as EffectivePermissions),
            this.failClosed(appId, agentFirebaseDbId),
          ),
        ),
      ),
    );
  }

  /** Convenience for consumers that only want the map. */
  permissionsMap(appId: EntityApplications): Observable<RoleEntityPermissionMap> {
    return this.forCurrentUser(appId).pipe(
      distinctUntilChanged((left, right) => left === right),
      switchMap(response => of(response.permissions)),
    );
  }

  /**
   * A failed permissions read must not take the app down with it, and must not
   * open it either: an unreachable identity API yields an EMPTY map, so the UI
   * offers nothing rather than everything. The server is still the enforcement
   * point, so a stale-open client would be refused anyway — but a stale-open UI
   * that lets a user start work the server will reject is worse than a plain
   * "you have no access" screen.
   *
   * Without this, a blocking bootstrap would hang forever whenever the API is down.
   */
  private failClosed(
    appId: EntityApplications,
    agentFirebaseDbId = '',
  ): MonoTypeOperatorFunction<EffectivePermissions> {
    return catchError((error: unknown) => {
      console.error('[permissions] failed to read effective permissions; treating as no access', error);

      // resolved: false — the UI shows nothing (fail closed), but nothing may
      // conclude from this that the user's access was REVOKED.
      return of(emptyPermissions(appId, agentFirebaseDbId, false));
    });
  }

  /**
   * Drives the initial fetch and every refresh.
   *
   * `startWith` is load-bearing, not cosmetic. The underlying Firestore document
   * watcher only subscribes once a user is signed in — CommonFireStoreDao.getDocument
   * gates on `onAuthStateChanged` — so while signed OUT it never emits and never
   * errors. Without a seed value the `combineLatest` below would never fire, which
   * left the blocking APP_INITIALIZER waiting forever and the app on a blank screen
   * before the login page had a chance to render.
   */
  private changeTrigger$(): Observable<number> {
    return this.domainSignals.getSignalVersion(DomainSignalId.userRoles).pipe(startWith(0), distinctUntilChanged());
  }

  private getCached(
    key: string,
    build: () => Observable<EffectivePermissions>,
  ): Observable<EffectivePermissions> {
    const cached = this.streamsByKey.get(key);
    if (cached) {
      return cached;
    }

    // refCount stays false: permissions are needed for the whole session, and
    // dropping the subscription would close the signal listener and force a refetch
    // the next time any guard or pipe asked.
    const stream$ = build().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.streamsByKey.set(key, stream$);

    return stream$;
  }
}
